import { createORPCClient, ORPCError } from '@orpc/client'
import { RPCLink } from '@orpc/client/fetch'
import type { ContractRouterClient } from '@orpc/contract'
import type { contract } from '@hub/contracts'
import { i18n } from '@/i18n'
import { recordClientEvent } from '@/lib/jam/client-events'

/**
 * Resolve an API path to a URL. With same-origin serving, this just
 * normalises the leading slash.
 */
export function toApiUrl(path: string): string {
  return path.startsWith('/') ? path : `/${path}`
}

const link = new RPCLink({
  url: new URL('/rpc', window.location.origin).toString(),
  // The Worker renders a few strings itself (emails, notification titles).
  headers: () => ({ 'x-hub-locale': i18n.language }),
  interceptors: [
    // Failed calls go to the client-event ring buffer (Jam bug reports):
    // the procedure name, error code and duration — never input or output.
    async ({ path, next }) => {
      const startedAt = Date.now()
      try {
        return await next()
      } catch (error) {
        recordClientEvent({
          kind: 'rpc',
          id: path.join('.'),
          code: error instanceof ORPCError ? error.code : undefined,
          at: startedAt,
          durationMs: Date.now() - startedAt,
        })
        throw error
      }
    },
  ],
})

export const orpc: ContractRouterClient<typeof contract> = createORPCClient(link)

/**
 * The credit budget refusal from `chat.triggerAgent` — returns which limit
 * was hit, or null for any other failure.
 */
export function creditsExhaustedReason(error: unknown): 'org' | 'project' | null {
  if (!(error instanceof ORPCError)) return null
  if (error.code !== 'CREDITS_EXHAUSTED') return null
  const data = error.data as { reason?: string } | undefined
  return data?.reason === 'project' ? 'project' : 'org'
}
